/**
 * 页面进入初始滚动定位（2026-09-22 需求）：
 * 长图页面设计稿高 1650px，普遍超出首屏，关键信息（组队按钮、海报按钮、回顾主体）
 * 可能落在首屏之外。本模块提供页面 mount 时一次性执行的定位能力，由各页面按需调用。
 *
 * 均为直接副作用（不做平滑滚动），在静态壳渲染完成后调用即可：
 * 页面 root 高度与主体坐标都是固定 PSD 几何（rem 等比缩放），不依赖接口数据。
 */

/**
 * 读取当前文档内容总高度
 * @param {Window} win
 * @returns {number} 无法读取时返回 0
 * @private
 */
function getDocumentScrollHeight(win) {
  const documentElement = win.document.documentElement;
  const body = win.document.body;
  return Math.max(documentElement ? documentElement.scrollHeight : 0, body ? body.scrollHeight : 0);
}

/**
 * 定位任务统一延后一帧执行：html 的 rem 基准（fontSize）由 AppRouter 在挂载完成后才设置，
 * React 挂载顺序是"子先父后"——页面 mount effect 同步执行 scrollTo 时 rem 布局尚未生效，
 * 文档高度不足会使滚动被浏览器收敛为 0（表现为居中/定位完全不生效，此前反复"没效果"的根源）；
 * rAF 回调时同步 didMount 均已跑完、fontSize 已设置，读取布局也会强制按新基准重排，
 * 首帧即滚到位、无可感知跳动。
 * @param {Function} task 定位回调
 * @private
 */
function runAfterLayout(task) {
  if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
    window.requestAnimationFrame(task);
  } else {
    task();
  }
}

/**
 * 将页面滚动到底部，使底部承载的关键操作（如首页「开始组队」按钮、海报页按钮区）进入首屏
 * @param {Window} [win=window] 目标窗口，缺省当前窗口；无效时静默跳过
 * @returns {void}
 * @example
 * // 页面组件内
 * useMount(() => scrollToPageBottom());
 */
export function scrollToPageBottom(win = window) {
  if (!win || !win.document || typeof win.scrollTo !== 'function') {
    return;
  }
  runAfterLayout(() => {
    const maxScrollTop = Math.max(0, getDocumentScrollHeight(win) - win.innerHeight);
    win.scrollTo(0, maxScrollTop);
  });
}

/**
 * 滚动使目标元素在视口中垂直居中（如回顾阶段页进入后展示回顾主体）
 * 超出可滚动范围时由浏览器自动收敛到边界
 * @param {Element|null} element 需要居中的 DOM 元素，查无元素时静默跳过
 * @param {Window} [win=window] 目标窗口
 * @returns {void}
 * @example
 * useMount(() => centerElementInViewport(document.querySelector('[data-name="回顾首页"]')));
 */
export function centerElementInViewport(element, win = window) {
  if (!element || typeof element.getBoundingClientRect !== 'function' || !win || typeof win.scrollTo !== 'function') {
    return;
  }
  runAfterLayout(() => {
    // rect 是视口坐标，叠加当前滚动量还原元素在文档中的绝对位置；
    // 路由入场动画仅做 X 轴平移（route-animate translateX），不影响此处 top/height 读数
    const rect = element.getBoundingClientRect();
    const currentScrollTop = win.pageYOffset || (win.document.documentElement && win.document.documentElement.scrollTop) || 0;
    const elementCenterInDocument = currentScrollTop + rect.top + rect.height / 2;
    win.scrollTo(0, Math.max(0, elementCenterInDocument - win.innerHeight / 2));
  });
}
